import Vuex from "vuex";
import Convince from "@/lib/Convince";
import ConvinceStub from "@/lib/ConvinceStub";
import Utilities from "@/lib/Utilities.js";
import { CookieStorage } from "cookie-storage";
import "url-search-params-polyfill";
import createPersistedState from "vuex-persistedstate";

let $convince;
if (!window.convince || typeof window.convince.start === "undefined") {
  if (window.convinceConfig.useProductionAPI) {
    $convince = new Convince(window.convinceConfig);
  } else {
    $convince = new ConvinceStub(window.convinceConfig);
  }
} else {
  $convince = window.convince;
}

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      key: $convince.config.campaignId + $convince.config.account,
      paths: ["answers", "signup"]
    })
  ],
  state: {
    index: 0,
    route: "",
    entries: 0,
    urlParams: {},
    config: window.cogniclick.config,
    session: {},
    responseUUID: null,
    resultURL: null,
    answers: {},
    static: {},
    calculations: {},
    scoreCache: {},
    scoreBoard: {},
    finalised: false,
    signup: null,
    signedUp: false,
    country: "GB",
    language: "EN",
    lang: {},
    supportedLanguages: ["EN"],
    stylesheet: null,
    status: null,
    wasResult: false,
    result: {}
  },
  mutations: {
    finalise(state) {
      state.finalised = true;
    },
    setRoute(state, payload) {
      state.route = payload;
    },
    setAnswer(state, payload) {
      state.answers[payload.key] = payload.answer.value;
      if (typeof payload.answer.score !== "undefined") {
        state.scoreBoard[payload.key] = payload.answer.score;
      }
    },
    setResult(state, payload) {
      state.result = payload;
    },
    setScores(state, payload) {
      state.scoreBoard = payload;
    },
    setResponseUUID(state, payload) {
      state.responseUUID = payload;
    },
    setResultURL(state, payload) {
      state.resultURL = payload;
    },
    setStatus(state, payload) {
      state.status = payload;
    },
    setAnswers(state, payload) {
      state.answers = payload;
    },
    setUrlParams(state, payload) {
      state.urlParams = payload;
    },
    setSignedUp(state) {
      state.signedUp = true;
    },
    signup(state, payload) {
      state.signup = payload;
    },
    advance(state) {
      state.index++;
    },
    setIndex(state, idx) {
      state.index = idx;
    },
    entries(state, count) {
      state.entries = count;
    },
    cacheScores(state, scores) {
      state.scoreCache = Object.assign({}, state.scoreCache, scores);
    },
    setSession(state, session) {
      state.session = session;
    },
    setStylesheet(state, stylesheet) {
      state.stylesheet = stylesheet;
    },
    setCountry(state, country) {
      state.country = country;
    },
    setLanguage(state, language) {
      const lang = language === "GB" ? "EN" : language;
      // eslint-disable-next-line no-unused-vars
      for (const languageDef of state.supportedLanguages) {
        if (languageDef.value === lang) {
          state.language = lang;
          break;
        }
      }
    },
    setLanguages(state, languageSettings) {
      state.lang = languageSettings.translations;
      state.supportedLanguages = languageSettings.supported;
    }
  },
  actions: {
    start({ commit, state }) {
      const cookieStorage = new CookieStorage();
      const sessionKey = "ccsession" + window.convinceConfig.campaignId;
      if (typeof state.session.respondentId === "undefined") {
        return $convince.start().then(function(resp) {
          state.session = resp.data;
          const cookieOptions = window.convinceConfig.isLocal
            ? {}
            : {
                secure: true,
                sameSite: "None"
              };
          cookieStorage.setItem(
            sessionKey,
            JSON.stringify(resp.data),
            cookieOptions
          );
        });
      } else {
        return new Promise(function(resolve, reject) {
          resolve();
        });
      }
    },
    async save({ commit, state }, payload) {
      commit("setAnswer", payload);
      const convinceData = {
        _s: payload.answer.score,
        _u: payload.key === "_userdata"
      };
      if (
        typeof state.answers[payload.key] === "object" &&
        state.answers[payload.key].constructor === Object
      ) {
        // unpack the object into the top level
        // eslint-disable-next-line no-unused-vars
        for (const prop in state.answers[payload.key]) {
          convinceData[prop] = state.answers[payload.key][prop];
        }
      } else {
        convinceData[payload.key] = state.answers[payload.key];
      }
      if (payload.answer._other) {
        convinceData._other = payload.answer._other;
      }
      // messy now. we need to merge custom user fields into the
      // main user object so we can do var replacement consistently
      return $convince.update(convinceData);
    },
    saveAll({ context, state }, payload) {
      // eslint-disable-next-line no-unused-vars
      for (const key in payload) {
        state.answers[key] = payload[key];
      }
      return $convince.update(payload);
    },
    saveLanguage({ context, state }, payload) {
      return $convince.update({ language: state.language });
    },
    getScores({ commit, state }) {
      const payload = { all: true };
      const promise = new Promise(function(resolve, reject) {
        $convince
          .scores(payload)
          .then((resp) => {
            commit("cacheScores", resp.data);
            resolve(state.scoreCache);
          })
          .catch((e) => {
            reject(e);
          });
      });
      return promise;
    },
    promoteUser({ commit, state }, payload) {
      const conversionImage = Utilities.dig(state.config, [
        "tracking",
        "linkedin",
        "events",
        "conversion"
      ]);
      if (typeof conversionImage !== "undefined" && conversionImage) {
        new Image(1, 1).src = conversionImage;
      }
      commit("setAnswer", payload);
      commit("setSignedUp");
      let convinceData = {};
      convinceData = payload.answer.value;
      return $convince.promoteUser(convinceData);
    },
    finalise({ commit, state }, payload) {
      commit("finalise");
      return $convince.finalise();
    },
    refreshSession({ commit, state }) {
      let reset = false;
      if (
        typeof state.config.reset !== "undefined" &&
        state.config.reset === true
      ) {
        reset = true;
      }

      const cookieStorage = new CookieStorage();

      const sessionKey = "ccsession" + window.convinceConfig.campaignId;

      if (reset && !state.wasReset) {
        cookieStorage.removeItem(sessionKey);
        state.session = {};
        $convince.config.responseId = null;
        state.wasReset = true;
      }

      const search = new URLSearchParams(window.location.search);
      const newParams = {};
      // eslint-disable-next-line no-unused-vars
      for (const entry of search.entries()) {
        const mappedEntry = {};
        mappedEntry[entry[0]] = entry[1];
        Object.assign(newParams, mappedEntry);
      }
      commit("setUrlParams", newParams);
      if (cookieStorage.getItem(sessionKey)) {
        const sessionData = JSON.parse(cookieStorage.getItem(sessionKey));
        $convince.config = sessionData;
        commit("setSession", Object.assign({}, sessionData));
        commit("setResponseUUID", sessionData.responseId);
      }
    }
  },
  getters: {
    convince(state) {
      if ($convince.setState) {
        $convince.setState(state);
      }
      return $convince;
    },
    account(state) {
      return state.config.account;
    },
    flattenedAnswers(state) {
      const base = JSON.parse(JSON.stringify(state.answers));
      // eslint-disable-next-line no-unused-vars
      for (const key in base._userdata) {
        base[key] = base._userdata[key];
      }
      return base;
    },
    questionSchema(state) {
      const config = state.config;
      const qs = {};
      const exclude = ["barrier", "user-info"];
      for (let i = 0; i < config.pages.length; i++) {
        const page = config.pages[i];
        if (page.type === "SurveyPage" && exclude.indexOf(page.id) === -1) {
          qs[page.options.key] = page.options.answers || null;
        }
      }
      return qs;
    },
    multilingual(state) {
      return state.supportedLanguages.length > 1;
    },
    currentPage(state, getters) {
      if (state.route === "result") {
        return state.config.pages[getters.lastPageIndex];
      }
      return state.config.pages[state.index];
    },
    lastPageIndex(state) {
      return state.config.pages.length - 1;
    },
    isLastPage(state, getters) {
      return Number(state.index) === getters.lastPageIndex;
    },
    isResultPage(state, getters) {
      return (
        getters.isLastPage ||
        state.route === "result" ||
        getters.currentPage.options.resultPage === true
      );
    },
    mode(state) {
      if (typeof state.config.mode !== "undefined") {
        return state.config.mode;
      } else {
        return "survey";
      }
    },
    questions(state) {
      const qs = [];
      if (typeof state.config === "undefined") {
        return qs;
      }
      for (let i = 0; i < state.config.pages.length; i++) {
        const page = state.config.pages[i];
        if (page.type === "SurveyPage") {
          qs.push(page.options);
        }
      }
      return qs;
    },
    questionKeys(state) {
      const keys = [];
      if (typeof state.config === "undefined") {
        return keys;
      }
      for (let i = 0; i < state.config.pages.length; i++) {
        const page = state.config.pages[i];
        if (page.type === "SurveyPage") {
          keys.push(page.options.key);
        }
      }
      return keys;
    },
    maxScore(state) {
      if (typeof state.config === "undefined") {
        throw new Error("Scoring: missing configuration");
      }
      let max = 0;
      for (let i = 0; i < state.config.pages.length; i++) {
        const page = state.config.pages[i];
        if (page.type === "SurveyPage" && page.options.scored === true) {
          if (page.options.type === "SingleSelect") {
            let maxAnswer = 0;
            page.options.answers.forEach((answer) => {
              if (answer.score > maxAnswer) {
                maxAnswer = answer.score;
              }
            });
            max += maxAnswer;
          }
          if (page.options.type === "MultiSelect") {
            page.options.answers.forEach((answer) => {
              max += answer.score;
            });
          }
        }
      }
      return max;
    }
  }
});
